import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { Task, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Drawer } from '../components/ui/Drawer'
import { Button } from '../components/ui/Button'
import { useProjectOptions } from '../hooks/useEntityOptions'

interface TaskWithDetails extends Task {
  assignee_name: string
  project_name: string
  project_slug: string
}

export default function Tasks() {
  const [tasks, setTasks] = useState<TaskWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [formData, setFormData] = useState({
    project_slug: '', title: '', description: '', priority: 'normal',
  })
  const [saving, setSaving] = useState(false)
  const { options: projectOptions } = useProjectOptions()

  const loadTasks = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.get<PaginatedResult<TaskWithDetails>>(
        `/tasks?limit=50${statusFilter ? `&status=${statusFilter}` : ''}`
      )
      setTasks(result.data)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [statusFilter])

  useEffect(() => { loadTasks() }, [loadTasks])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/tasks', formData)
      setDrawerOpen(false)
      setFormData({ project_slug: '', title: '', description: '', priority: 'normal' })
      loadTasks()
    } catch { /* error */ }
    finally { setSaving(false) }
  }

  async function toggleStatus(task: TaskWithDetails) {
    const nextStatus = task.status === 'done' ? 'todo' : 'done'
    try {
      await api.patch(`/tasks/${task.slug}`, { status: nextStatus })
      loadTasks()
    } catch { /* error */ }
  }

  function formatDate(iso: string | null) {
    if (!iso) return null
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  if (loading) return <TableSkeleton cols={4} />

  const STATUS_FILTERS = ['', 'todo', 'in_progress', 'blocked', 'done']

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Tasks</h1>
        <Button onClick={() => setDrawerOpen(true)}
          icon={<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>}
        >
          <span className="hidden sm:inline">New Task</span>
          <span className="sm:hidden">New</span>
        </Button>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className="shrink-0 px-3 py-1.5 rounded-full transition-colors"
            style={{
              fontSize: 12,
              fontWeight: 500,
              background: statusFilter === s ? 'var(--accent)' : 'var(--bg-inset)',
              color: statusFilter === s ? '#fff' : 'var(--text-secondary)',
            }}
          >
            {s ? s.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      {tasks.length === 0 ? (
        <EmptyState
          title="No tasks"
          description="Create tasks to keep track of project work."
          action={<Button onClick={() => setDrawerOpen(true)}>New Task</Button>}
        />
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className="card flex items-start gap-3">
              <button
                onClick={() => toggleStatus(t)}
                className="mt-0.5 shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors"
                style={{
                  border: `2px solid ${t.status === 'done' ? 'var(--success)' : 'var(--border)'}`,
                  background: t.status === 'done' ? 'var(--success)' : 'transparent',
                }}
              >
                {t.status === 'done' && (
                  <svg className="w-3 h-3" style={{ color: '#fff' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
              </button>

              <div className="flex-1 min-w-0">
                <p style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: t.status === 'done' ? 'var(--text-tertiary)' : 'var(--text)',
                  textDecoration: t.status === 'done' ? 'line-through' : 'none',
                }}>
                  {t.title}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.project_name}</span>
                  <StatusBadge status={t.priority} />
                  {t.due_date && (
                    <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDate(t.due_date)}</span>
                  )}
                </div>
              </div>

              {t.status !== 'done' && <StatusBadge status={t.status} />}
            </div>
          ))}
        </div>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New Task">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Title *</label>
            <input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input"
              required
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Project *</label>
            <select
              value={formData.project_slug}
              onChange={(e) => setFormData({ ...formData, project_slug: e.target.value })}
              className="input"
              required
              style={{ fontSize: 14 }}
            >
              <option value="">Select project...</option>
              {projectOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="input"
              style={{ fontSize: 14 }}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input"
              rows={3}
              style={{ fontSize: 14 }}
            />
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%', fontSize: 14 }}>
            Create Task
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
